'use es6';

import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import Actions from 'AdsUI/lib/Actions';
import FormattedCurrency from 'I18n/components/FormattedCurrency';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import I18n from 'I18n';
import PortalIdParser from 'PortalIdParser';
import getErrorNotificationBody, { getPermissionsError } from './ErrorNotificationBody';
import { PERMISSIONS_ERROR_STRINGS, EVENT_CREATION_ERROR_TYPES, EVENT_CREATION_RESPONSE_MESSAGE_KEYWORDS } from 'AdsUI/lib/Constants';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import PartialErrorHandler from './PartialErrorHandler';
import getLeadAdCreationErrorBody from './LeadAdCreationErrorBody';
import getAudienceCreationError from './getAudienceCreationError';
import { getAdAccounts } from 'AdsUI/selectors/AccountsSelectors';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILink from 'UIComponents/link/UILink';
import UIButton from 'UIComponents/button/UIButton';
const settingsURL = `/settings/${PortalIdParser.get()}/marketing/ads`;
const KeywordErrorMessageToI18nKey = {
  'UrlError.MISSING_PROTOCOL': 'errors.adwords.malformedUrlMissingProtocol',
  'UrlError.INVALID_TLD': 'errors.adwords.malformedUrlInvalidTld',
  'UrlError.MISSING_HOST': 'errors.adwords.malformedUrlInvalidTld',
  'AdGroupCriterionError.CANNOT_SET_WITHOUT_FINAL_URLS': 'errors.adwords.missingFinalUrl'
}; // Potential audience creation errors _not_ due to any issues with the audience itself.

const AUDIENCE_ERROR_TYPE_MAP = {
  '(#2654) Source is Too Small: Please choose a source that includes at least 100 people in the same country.': 'SOURCE_IS_TOO_SMALL',
  'This portal has been deemed untrustworthy based on an automated security monitoring system.': 'UNTRUSTWORTHY_PORTAL',
  'Advertiser not whitelisted for using uploaded data': 'ADVERTISER_NOT_WHITELISTED_FOR_USING_UPLOADED_DATA',
  // TODO: mpape temporary until BE sorts out
  'UserListError.ADVERTISER_NOT_WHITELISTED_FOR_USING_UPLOADED_DATA': 'ADVERTISER_NOT_WHITELISTED_FOR_USING_UPLOADED_DATA'
};
export const KNOWN_AUDIENCE_ERRORS = {
  SEED_AUDIENCE_TOO_SMALL: 'SEED_AUDIENCE_TOO_SMALL',
  ADVERTISER_NOT_WHITELISTED_FOR_USING_UPLOADED_DATA: 'ADVERTISER_NOT_WHITELISTED_FOR_USING_UPLOADED_DATA'
};
const AUDIENCE_ERROR_TO_TRACKING_MAP = {
  SOURCE_IS_TOO_SMALL: 'Source is too small',
  UNTRUSTWORTHY_PORTAL: 'Untrustworthy portal',
  ADVERTISER_NOT_WHITELISTED_FOR_USING_UPLOADED_DATA: 'Google ad account not whitelisted',
  SEED_AUDIENCE_TOO_SMALL: 'Seed audience too small to create lookalike'
}; // actions that should not be shown a generic failure
// TODO: these actions are starting to accumulate, is there perhaps an
// opportunity for refactoring here.

const genericFailureBlacklist = [Actions.ADWORDS_AD_CREATE_FAILED, Actions.FB_GEOLOCATIONS_SEARCH_FAILED, Actions.LEAD_FORMS_FETCH_FAILED];
const OVERRIDE_ERROR_MESSAGE = {
  '(#17) User request limit reached null null': 'errors.facebook.rateLimitError'
};
const KNOWN_AD_FAILURES = ['BUDGET_TOO_LOW'];
const KNOWN_AD_FAILURES_PARTIAL_MATCH = ['Custom audience terms not accepted'];
const NETWORK_API_CALLS_DISABLED = 'NETWORK_API_CALLS_DISABLED';

const errorTextKey = responseJSON => {
  if (I18n.lookup(`errors.${responseJSON.errorType}`)) {
    return `errors.${responseJSON.errorType}`;
  }

  if (OVERRIDE_ERROR_MESSAGE[responseJSON.message]) {
    return OVERRIDE_ERROR_MESSAGE[responseJSON.message];
  }

  return null;
};

const getAdAccount = ({
  accountId,
  adNetwork,
  getState
}) => {
  const adAccounts = getAdAccounts(getState());
  return adAccounts.find(account => account.accountId === accountId && account.network === adNetwork);
}; //  Action objects are the objects passed by a dispatch. to see standard actions,
//  take a look at ActionWrapper. Otherwise it can also be passed explicitly


const genericFailureObj = function genericFailureObj(action) {
  if (genericFailureBlacklist.indexOf(action.type) !== -1) {
    return undefined;
  }

  if (action.meta && action.meta.responseJSON) {
    const responseJSON = action.meta.responseJSON;
    const details = responseJSON.message || responseJSON.errorType;
    const {
      errorType,
      errorSubtype
    } = responseJSON;
    const textErrorKey = errorTextKey(responseJSON);
    const text = textErrorKey ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: textErrorKey,
      options: {
        settingsURL
      }
    }) : undefined;
    return getErrorNotificationBody({
      actionType: action.type,
      text,
      details,
      errorType,
      errorSubtype
    });
  }

  return getErrorNotificationBody({
    actionType: action.type
  });
};

const Networks = {
  ADWORDS: 'Google',
  FACEBOOK: 'Facebook',
  LINKEDIN: 'LinkedIn'
};

const networkOutageObj = function networkOutageObj(action) {
  if (action.meta && action.meta.responseJSON) {
    const responseJSON = action.meta.responseJSON;
    const {
      errorType,
      adNetwork
    } = responseJSON;

    const title = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "errors.networkOutage.title",
      options: {
        adNetwork: Networks[adNetwork]
      }
    });

    const text = /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "errors.networkOutage.text",
      options: {
        adNetwork: Networks[adNetwork]
      }
    });

    return getErrorNotificationBody({
      actionType: action.type,
      text,
      errorType,
      title
    });
  }

  return getErrorNotificationBody({
    actionType: action.type
  });
};
/**
 * Parse the relevant portion of LinkedIn's "total budget too low" error
 * message. "Relevant" here means the sentence that contains the user's desired
 * budget and LinkedIn's minimum budget for their ad.
 * @param {string} errorMessage The error message from the API response
 * @returns The relevant sentence or null
 */


const parseLinkedInTotalBudgetTooLowError = errorMessage => {
  // Case-insensitive match for "/Campaign/totalBudget/amount value 10.0 cannot be lower than 650.000"
  const regex = /\/campaign\/totalbudget\/amount value [0-9]+\.[0-9]* cannot be lower than [0-9]+\.[0-9]*/i;
  const regexMatches = regex.exec(errorMessage);
  return regexMatches.length ? regexMatches[0] : null;
};
/**
 * Determine if the error message corresponds to a LinkedIn "total budget too
 * low" error.
 * @param {string} errorMessage The error message from the API response
 */


const isLinkedInTotalBudgetTooLowError = errorMessage => {
  return /linkedin/i.test(errorMessage) && !!parseLinkedInTotalBudgetTooLowError(errorMessage);
};
/**
 * Generate the notification that indicates the user's total budget is too low.
 * @param {*} action The Redux action
 * @param {string} errorMessage The LinkedIn error message
 * @returns The notification to display to the user
 */


const generateLinkedInTotalBudgetTooLowNotification = (action, errorMessage) => {
  const totalBudgetTooLowMessage = parseLinkedInTotalBudgetTooLowError(errorMessage); // First element will be the user's desired budget and second will be LinkedIn's min. for this ad

  const userBudgetAndMinBudget = totalBudgetTooLowMessage.match(/[0-9]+\.[0-9]*/g);
  return getErrorNotificationBody({
    actionType: action.type,
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "errors.linkedIn.totalBudgetTooLow.title"
    }),
    text: /*#__PURE__*/_jsxs("span", {
      children: [/*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "errors.linkedIn.totalBudgetTooLow.details_jsx",
        elements: {
          FormattedCurrency
        },
        options: {
          value: userBudgetAndMinBudget[1],
          currencyCode: action.payload.currencyCode
        }
      }), "\xA0", /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "errors.linkedIn.totalBudgetTooLow.context_jsx",
        elements: {
          Link: UILink
        },
        options: {
          external: true,
          href: 'https://www.linkedin.com/help/lms/answer/a422101'
        }
      })]
    })
  });
};

const adFailureObj = action => {
  const {
    meta: {
      responseJSON,
      responseJSON: {
        errorType = null
      }
    },
    type
  } = action;

  if (KNOWN_AD_FAILURES.includes(errorType)) {
    return undefined;
  }

  let shouldShowError = true;
  KNOWN_AD_FAILURES_PARTIAL_MATCH.forEach(failureType => {
    if (errorType.includes(failureType)) {
      shouldShowError = false;
    }
  });

  if (!shouldShowError) {
    return undefined;
  }

  if (errorType === NETWORK_API_CALLS_DISABLED) {
    return networkOutageObj(action);
  }

  return getLeadAdCreationErrorBody(type, errorType, responseJSON) || genericFailureObj(action);
};

export default {
  genericFailure: action => {
    return genericFailureObj(action);
  },
  [Actions.FACEBOOK_CURRENT_USER_TERMS_OF_SERVICE_ERROR]: action => {
    return getErrorNotificationBody({
      actionType: action.type,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "creation.adSequenceToSTitle"
      }),
      text: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "creation.adSequenceCurrentUserToS_jsx",
          elements: {
            Link: UILink
          },
          options: {
            href: action.payload.href,
            external: true,
            use: 'on-bright'
          }
        }), /*#__PURE__*/_jsx(UIButton, {
          use: "tertiary",
          className: "m-left-4",
          onClick: action.payload.onAcceptTos,
          size: "extra-small",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "creation.acceptToSButton"
          })
        })]
      }),
      id: Actions.FACEBOOK_CURRENT_USER_TERMS_OF_SERVICE_ERROR
    });
  },
  [Actions.FACEBOOK_PRIMARY_USER_TERMS_OF_SERVICE_ERROR]: action => {
    return getErrorNotificationBody({
      actionType: action.type,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "creation.adSequenceToSTitle"
      }),
      text: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "creation.adSequencePrimaryUserToS_jsx",
        elements: {
          Link: UILink
        },
        options: {
          name: action.payload.name,
          href: action.payload.href,
          external: true,
          use: 'on-bright'
        }
      })
    });
  },
  [Actions.OFFLINE_EVENT_CREATE_FAILED]: action => {
    if (action.meta && action.meta.responseJSON && action.meta.responseJSON.errorType === EVENT_CREATION_ERROR_TYPES.APP_SCOPE && action.meta.responseJSON.message.includes(EVENT_CREATION_RESPONSE_MESSAGE_KEYWORDS.MODIFY_OFFLINE_CONVERSION_DATA_SETS)) {
      return undefined;
    } else return genericFailureObj(action);
  },
  [Actions.AUDIENCE_CREATE_FAILED]: (action, getState) => {
    const {
      meta: {
        responseJSON = {}
      },
      payload: {
        options: {
          accountId,
          adNetwork
        }
      }
    } = action;
    const {
      message = '',
      errorType = ''
    } = responseJSON;
    const details = message || errorType;

    if (details && PERMISSIONS_ERROR_STRINGS.some(error => details.indexOf(error) > -1)) {
      return getPermissionsError({
        network: adNetwork,
        adAccount: getAdAccount({
          accountId,
          adNetwork,
          getState
        })
      });
    } else if (details && (Object.keys(AUDIENCE_ERROR_TYPE_MAP).includes(details) || Object.keys(KNOWN_AUDIENCE_ERRORS).includes(errorType))) {
      const error = Object.keys(KNOWN_AUDIENCE_ERRORS).includes(errorType) ? errorType : AUDIENCE_ERROR_TYPE_MAP[details];
      usageTracker.track('modifyAudience', {
        action: 'Failed to create audience',
        error: AUDIENCE_ERROR_TO_TRACKING_MAP[error]
      });
      return getAudienceCreationError({
        network: adNetwork,
        error,
        adAccount: getAdAccount({
          accountId,
          adNetwork,
          getState
        })
      });
    }

    return genericFailureObj(action);
  },
  [Actions.CAMPAIGN_LIST_FETCH_SUCCEEDED]: PartialErrorHandler('errors.partialErrors.errorFetchingCampaigns', 'errors.partialErrors.errorFetchingCampaignsError'),
  [Actions.CAMPAIGN_LIST_EARLY_REQUEST_FETCH_SUCCEEDED]: PartialErrorHandler('errors.partialErrors.errorFetchingCampaigns', 'errors.partialErrors.errorFetchingCampaignsError'),
  [Actions.AUDIENCES_FETCH_SUCCEEDED]: PartialErrorHandler('errors.partialErrors.errorFetchingAudiences', 'errors.partialErrors.errorFetchingAudiencesError'),
  [Actions.AUDIENCES_INACTIVE_FETCH_SUCCEEDED]: PartialErrorHandler('errors.partialErrors.errorFetchingAudiences', 'errors.partialErrors.errorFetchingAudiencesError'),
  [Actions.PIXELS_FETCH_SUCCEEDED]: PartialErrorHandler('errors.partialErrors.errorFetchingPixels', 'errors.partialErrors.errorFetchingPixelsError'),
  [Actions.AD_EXTENSION_CREATE_FAILED]: action => {
    return getErrorNotificationBody({
      actionType: action.type,
      text: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "errors.adExtensionCreateFailed",
        options: {
          code: action.meta.responseJSON.message
        }
      })
    });
  },
  [Actions.KEYWORD_BULK_CREATE_FAILED]: action => {
    if (action.meta.responseJSON.message === 'PolicyViolationError.POLICY_ERROR') {
      return getErrorNotificationBody({
        actionType: action.type,
        text: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "errors.adwords.policyErrorKeyword"
        })
      });
    }

    return genericFailureObj(action);
  },
  [Actions.KEYWORD_CREATE_FAILED]: action => {
    if (action.meta.responseJSON.message === 'PolicyViolationError.POLICY_ERROR') {
      return getErrorNotificationBody({
        actionType: action.type,
        text: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "errors.adwords.policyErrorKeyword"
        })
      });
    }

    return genericFailureObj(action);
  },
  [Actions.ADWORDS_KEYWORD_UPDATE_FAILED]: action => {
    /* Possible errors (so far) are:
         {UrlError.INVALID_TLD_IN_FINAL_URL, UrlError.INVALID_TLD_IN_FINAL_MOBILE_URL},
         {UrlError.MISSING_PROTOCOL_IN_FINAL_URL, UrlError.MISING_PROTOCOL_IN_FINAL_MOBILE_URL},
         {UrlError.MISSING_HOST_IN_FINAL_URL, UrlError.MISSING_PROTOCOL, UrlError.MISSING_HOST_IN_FINAL_MOBILE_URL},
         and {AdGroupCriterionError.CANNOT_SET_WITHOUT_FINAL_URLS}
    */
    const {
      message
    } = action.meta.responseJSON;
    const errorText = KeywordErrorMessageToI18nKey[Object.keys(KeywordErrorMessageToI18nKey).find(error => message.indexOf(error) > -1)];

    if (errorText) {
      return getErrorNotificationBody({
        actionType: action.type,
        text: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: errorText
        })
      });
    }

    return genericFailureObj(action);
  },
  [Actions.CAMPAIGN_REPORTING_ENTITY_UPDATE_FAILED]: action => {
    const {
      payload: {
        network,
        adAccount
      },
      meta: {
        responseJSON: {
          message = '',
          errorType = ''
        }
      }
    } = action;
    const details = message || errorType;

    if (details && PERMISSIONS_ERROR_STRINGS.some(error => details.indexOf(error) > -1)) {
      return getPermissionsError({
        network,
        adAccount
      });
    }

    if (message.indexOf('CampaignError.CANNOT_MODIFY_FOR_TRIAL_CAMPAIGN') > -1) {
      return getErrorNotificationBody({
        actionType: action.type,
        text: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "errors.adwords.draftCampaign"
        })
      });
    }

    if (network === Networks.FACEBOOK && message.indexOf('Invalid Parameter') > -1) {
      return getErrorNotificationBody({
        actionType: action.type,
        text: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "errors.facebook.invalidParameter"
        }),
        details: errorType
      });
    }

    return genericFailureObj(action);
  },
  [Actions.LEAD_AD_CREATE_FAILED]: action => {
    const message = action.meta.responseJSON.message;

    if (isLinkedInTotalBudgetTooLowError(message)) {
      return generateLinkedInTotalBudgetTooLowNotification(action, message);
    }

    return adFailureObj(action);
  },
  [Actions.LEAD_FORMS_CREATE_FAILED]: adFailureObj,
  [Actions.SEQUENCE_CREATE_FAILED]: adFailureObj,
  [Actions.SEQUENCE_UPDATE_FAILED]: adFailureObj,
  [Actions.CAMPAIGN_DETAILS_FETCH_FAILED]: action => Object.assign({}, genericFailureObj(action), {
    id: action.type
  })
};